<template>
  <form @submit.prevent="submit">
    <div class="mb-2 flex grid-cols-3 flex-col gap-2 md:mb-4 md:grid md:gap-4">
      <SelectField
        name="rentalPartner"
        :label="t('rentalPartner')"
        :options="rentalPartnerOptions"
        empty-value-to="null"
      />
      <InputField :label="t('reservationNumber')" name="comment" />
    </div>
    <div>
      <BookingRangePicker
        :start-date="formValues.startDate"
        :end-date="formValues.endDate"
        class="col-span-2"
        :car-id="props.car.id"
        :other-price-changing-module-is-active="false"
        :start-date-title="t('start')"
        :end-date-title="t('end')"
        @update:start-date="setFieldValue('startDate', $event)"
        @update:end-date="setFieldValue('endDate', $event)"
      />

      <Divider />

      <VehicleImmutable :car="car" />

      <Divider />

      <div class="flex justify-end"></div>

      <div class="flex items-center">
        <CVButton
          v-if="!allocationNew"
          variant="danger"
          :disabled="isSaving"
          :is-loading="isDeleting"
          @click.prevent="$emit('onDelete')"
        >
          {{ t('deleteBooking') }}
        </CVButton>

        <div class="flex flex-1 justify-end">
          <CVButton
            variant="default"
            type="submit"
            class="justify-self-end"
            :disabled="isDeleting"
            :is-loading="isSaving"
          >
            {{ t('save') }}
          </CVButton>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import InputField from '@/components/InputField.vue';
import { useI18n } from 'vue-i18n';
import type { Car } from '@/entities/cars/car.entity';
import { computed } from 'vue';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import { DateTime } from 'luxon';
import VehicleImmutable from '@/components/booking-forms/VehicleImmutable.vue';
import SelectField from '@/components/SelectField.vue';
import { RentalPartner } from '@/entities/bookings/rental-partner.enum';
import { partnerRentSchema } from '@/validation/partner-rent.schema';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import type { UpdateAllocationDto } from '@carvia/ros-client-types';

const props = defineProps<{
  car: Car;
  allocation?: Allocation;
  allocationNew: boolean;
  isSaving: boolean;
  isDeleting: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateAllocationDto): void;
  (e: 'onDelete'): void;
}>();

const { t } = useI18n();

interface FormValues {
  startDate: string;
  endDate: string;
}

const rentalPartnerOptions = computed(
  () =>
    Object.values(RentalPartner).map((rentalPartner) => ({
      label: t(`rentalPartners.${rentalPartner}`),
      value: rentalPartner,
    })) ?? [],
);

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm({
  initialValues: {
    rentalPartner: props.allocation?.rentalPartner ?? RentalPartner.PORSCHE_DRIVE,
    comment: props.allocation?.comment,
    startDate:
      props.allocation?.startDate ??
      DateTime.now().set({ hour: 11, minute: 0, second: 0, millisecond: 0 }).toISO(),
    endDate:
      props.allocation?.endDate ??
      DateTime.now()
        .set({ hour: 11, minute: 0, second: 0, millisecond: 0 })
        .plus({ day: 1 })
        .toISO(),
  },
  validationSchema: partnerRentSchema,
});

const submit = handleSubmit((values) => {
  emit('onSubmit', {
    ...values,
    startDate: new Date(values.startDate),
    endDate: new Date(values.endDate),
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "reservationNumber": "Reservation Number",
    "save": "Save",
    "start": "Start",
    "end": "End",
    "deleteBooking": "Delete Booking",
    "rentalPartner": "Rental Partner"
  },
  "de": {
    "reservationNumber": "Reservierungsnummer",
    "save": "Speichern",
    "start": "Beginn",
    "end": "Ende",
    "deleteBooking": "Buchung löschen",
    "rentalPartner": "Mietpartner"
  },
  "es": {
    "reservationNumber": "N.º de reserva",
    "save": "Guardar",
    "start": "Inicio",
    "end": "Fin",
    "deleteBooking": "Eliminar reserva",
    "rentalPartner": "Partner de alquiler"
  }
}
</i18n>
